import { Box, Typography, Paper, Grid, Stack, useTheme, Tooltip } from "@mui/material";
import { formatPhone } from 'utils/phone'
import { useTranslation } from "react-i18next";
import { PersonOutline, FamilyRestroomOutlined, AccountCircleOutlined, BadgeOutlined, Verified, HighlightOff } from "@mui/icons-material";
import dayjs from "dayjs";


const safeJoin = (...parts) =>
  parts.map((p) => (p ?? "").toString().trim()).filter(Boolean).join(", ");

const SectionHeader = ({ icon: Icon, title, status }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: '1px dashed #e0e0e0' }}>
    <Icon color="action" fontSize="small" />
    <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
      {title}
    </Typography>
    {status}
  </Box>
);

const InfoItem = ({ label, value }) => (
  <Box sx={{ width: '100%', minWidth: 0 }}>
    <Typography variant="body2" display="block" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
      {value || "—"}
    </Typography>
  </Box>
);

export default function ApplicantInfo({ applicantInfo }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const checks = applicantInfo?.applicationSchools?.map(item => item.siblingCheck) ?? []
  const isSibling = checks.some(v => v === true) ? true
    : checks.some(v => v === false) ? false
      : null
      
  const isStaffVerified = applicantInfo?.staffPriority
    ? applicantInfo?.staffChecked === true ? true
      : applicantInfo?.staffChecked === false ? false
        : null
    : null
  if (!applicantInfo) return null;
  const addressLine = safeJoin(
    applicantInfo.address?.street,
    applicantInfo.address?.line2,
    safeJoin(applicantInfo.address?.city, applicantInfo.address?.state).trim(),
    applicantInfo.address?.zip
  );

  const studentName = [applicantInfo.firstName, applicantInfo.middleName, applicantInfo.lastName].filter(Boolean).join(" ");
  const parentName = `${applicantInfo.parentFirstName} ${applicantInfo.parentLastName}`;
  const siblingName = applicantInfo.siblingFirstName ? `${applicantInfo.siblingFirstName} ${applicantInfo.siblingLastName}` : "-";

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        mb: 3,
        overflow: "hidden"
      }}
    >

      <Box sx={{ px: 3, py: 2, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
          {t("Personal Information")}
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} py={2}>

          <Grid item xs={12} md={6}>
            <SectionHeader icon={PersonOutline} title={t("Student Details")} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoItem label={t("Application ID")} value={applicantInfo.id} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem label={t("Full Name")} value={studentName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem label={t("Date of Birth")} value={dayjs(applicantInfo.dateOfBirth).format("MMMM D, YYYY")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem label={t("Home Address")} value={addressLine} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem label={t("School Year")} value={applicantInfo.academicYearProvider?.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoItem label={t("Grade Applying")} value={applicantInfo.studentGr?.name} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6} sx={{ borderLeft: { md: `1px solid ${theme.palette.divider}` }, pl: { md: 4 } }}>

            <Box sx={{ mb: 3 }}>
              <SectionHeader icon={AccountCircleOutlined} title={t("Parent / Guardian")} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoItem label={t("Name")} value={parentName} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <InfoItem label={t("Phone")} value={formatPhone(applicantInfo.parentPhone, '-')} />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <InfoItem label={t("Email")} value={applicantInfo.parentEmail} />
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <SectionHeader
                icon={FamilyRestroomOutlined}
                title={t("Sibling")}
                status={
                  isSibling === true ? (
                    <Tooltip title={t("Sibling information has been verified")} arrow>
                      <Verified color="success" fontSize="small" />
                    </Tooltip>
                  ) : isSibling === false ? (
                    <Tooltip title={t("Sibling verified but not eligible")} arrow>
                      <HighlightOff color="error" fontSize="small" />
                    </Tooltip>
                  ) : null
                }
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoItem label={t("Name")} value={siblingName} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem label={t("Current Grade")} value={applicantInfo.siblingGr?.name || applicantInfo.siblingGrade || "-"} />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 3 }}>
              <SectionHeader
                icon={BadgeOutlined}
                title={t("Staff Priority")}
                status={
                  isStaffVerified === true ? (
                    <Tooltip title={t("Staff employment has been verified")} arrow>
                      <Verified color="success" fontSize="small" />
                    </Tooltip>
                  ) : isStaffVerified === false ? (
                    <Tooltip title={t("Staff employment could not be verified")} arrow>
                      <HighlightOff color="error" fontSize="small" />
                    </Tooltip>
                  ) : null
                }
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoItem label={t("Requested")} value={applicantInfo.staffPriority ? t("Yes") : t("No")} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem label={t("Work Email")} value={applicantInfo.staffEmail} />
                </Grid>
              </Grid>
            </Box>

          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
